// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  createTeamAchievement,
  deleteTeamAchievement,
  editTeamAchievement,
  getTeamAchievementDetail,
  getTeamAchievementList,
  toggleTeamAchievementActive,
} from '@/modules/team-achievement/team-achievement.service'
import type {
  TCreateTeamAchievementRequest,
  TEditTeamAchievementRequest,
  TTeamAchievement,
  TToggleTeamAchievementActiveRequest,
} from '@/modules/team-achievement/team-achievement.types'

export const teamAchievementListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['teamAchievementList', params],
    queryFn: () => getTeamAchievementList(params),
  })

export const teamAchievementDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['teamAchievementDetail', id],
    queryFn: () => getTeamAchievementDetail(id),
    staleTime: 0,
  })

export function useCreateTeamAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TCreateTeamAchievementRequest>) {
  return useMutation<void, TResponseError, TCreateTeamAchievementRequest>({
    mutationFn: createTeamAchievement,
    onSuccess,
    onError,
  })
}

export function useEditTeamAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TEditTeamAchievementRequest>) {
  return useMutation<void, TResponseError, TEditTeamAchievementRequest>({
    mutationFn: editTeamAchievement,
    onSuccess,
    onError,
  })
}

export function useToggleTeamAchievementActive({
  onSuccess,
  onError,
}: TMutation<
  TTeamAchievement,
  TResponseError,
  TToggleTeamAchievementActiveRequest
>) {
  return useMutation<
    TTeamAchievement,
    TResponseError,
    TToggleTeamAchievementActiveRequest
  >({
    mutationFn: toggleTeamAchievementActive,
    onSuccess,
    onError,
  })
}

export function useDeleteTeamAchievement({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteTeamAchievement,
    onSuccess,
    onError,
  })
}
